import { Tooltip, Typography } from '@pulse/ui';
import type { ComponentProps } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

export const LINE_CLAMP_3_SX = {
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
} as const;

export const LINE_CLAMP_2_SX = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
} as const;

// Detects whether the element is actually overflowing (single-line ellipsis: width; line-clamp:
// height) so a tooltip can be shown only when there's hidden text to reveal. Runs in
// `useLayoutEffect` (synchronously after DOM mutations, before paint) so the very first hover
// can't land on a not-yet-measured element.
export function useIsTruncated<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, isTruncated };
}

type TypographyPropsLite = ComponentProps<typeof Typography>;
type TooltipPropsLite = ComponentProps<typeof Tooltip>;

export interface TruncatableTextProps {
  text: string;
  sx?: TypographyPropsLite['sx'];
  variant?: TypographyPropsLite['variant'];
  color?: TypographyPropsLite['color'];
  fontWeight?: TypographyPropsLite['fontWeight'];
  noWrap?: boolean;
  placement: TooltipPropsLite['placement'];
}

// Renders text truncated (via `noWrap` or a line-clamp `sx`) and reveals it via `Tooltip` when
// the rendered element is actually overflowing. The `Tooltip` is always mounted — an empty
// `title` renders nothing (MUI's documented way to conditionally disable a tooltip) — rather
// than conditionally mounting/unmounting it, which would remount the underlying element (and
// detach the overflow-measuring ref/observer) the instant truncation is first detected.
//
// `display: 'block'` is forced (overridable via `sx`) because `maxWidth`/`width` have no effect
// on inline elements — MUI maps some variants (e.g. `caption`, `overline`) to an inline `<span>`
// by default, which would silently ignore any `maxWidth` cap here and never actually truncate.
export function TruncatableText({ text, sx, variant, color, fontWeight, noWrap, placement }: TruncatableTextProps) {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>();
  return (
    <Tooltip title={isTruncated ? text : ''} placement={placement}>
      <Typography
        ref={ref}
        variant={variant}
        color={color}
        fontWeight={fontWeight}
        noWrap={noWrap}
        sx={{ display: 'block', ...sx }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
}
